import React, { useState, useEffect, useCallback } from 'react'
import styles from './AmenitiesCarousel.module.css'

// Replace with actual imports:
import img1 from '../../assets/amenity1.jpg'
import img2 from '../../assets/amenity2.jpg'
import img3 from '../../assets/amenity3.jpg'
import img4 from '../../assets/amenity4.jpg'
import img5 from '../../assets/amenity5.jpg'

const slides = [
    {
        title: 'Host at a Different Scale',
        description: 'An expansive setting for refined gatherings, where every occasion carries the elegance of a grand address.',
        image: img1,
        tag: 'Grand Ballroom',
        level: 'Level 1',
    },
    {
        title: 'Where Life Takes Stage',
        description: 'Rising from Level 1 to Level 2, a thoughtfully designed space for experiences that feel larger than everyday life.',
        image: img2,
        tag: 'Sky Terrace',
        level: 'Level 1–2',
    },
    {
        title: 'Terrace Level 2',
        description: 'Open views and quiet spaces designed for downtime, where the city skyline becomes your backdrop.',
        image: img3,
        tag: 'Open Terrace',
        level: 'Level 2',
    },
    {
        title: 'A Setting for Everyday Indulgence',
        description: 'A setting designed for unhurried moments, where conversations linger and time feels easy to go.',
        image: img4,
        tag: 'Residents Lounge',
        level: 'Level 2',
    },
    {
        title: 'An Address Like No Other',
        description: 'Every corner crafted with intention — a living experience that goes beyond the expected.',
        image: img5,
        tag: 'Sky Lounge',
        level: 'Level 3',
    },
]


const AmenitiesCarousel = () => {
    const [current, setCurrent] = useState(0)
    const [animating, setAnimating] = useState(false)
    const [direction, setDirection] = useState('next')

    const goTo = useCallback((index, dir = 'next') => {
        if (animating) return
        setDirection(dir)
        setAnimating(true)
        setTimeout(() => {
            setCurrent(index)
            setAnimating(false)
        }, 500)
    }, [animating])

    const prev = () => {
        const index = (current - 1 + slides.length) % slides.length
        goTo(index, 'prev')
    }

    const next = () => {
        const index = (current + 1) % slides.length
        goTo(index, 'next')
    }

    useEffect(() => {
        const timer = setInterval(() => next(), 6000)
        return () => clearInterval(timer)
    }, [current])

    const slide = slides[current]

    return (
        <section className={styles.section}>

            {/* ── Section Header ── */}
            <div className={styles.header}>
                <span className={styles.eyebrow}>
                    <span className={styles.eyebrowLine}></span>
                    LIFESTYLE
                </span>
                <h2 className={styles.sectionTitle}>
                    An Elevated Life,{' '}
                    <em>Beyond The Residence</em>
                </h2>
            </div>

            {/* ── Carousel ── */}
            <div className={styles.carousel}>

                {/* Left: Text Panel */}
                <div className={styles.textPanel}>
                    <div className={styles.tagRow}>
                        <span className={styles.tag}>{slide.tag}</span>
                        <span className={styles.level}>{slide.level}</span>
                    </div>
                    <h3
                        className={`${styles.slideTitle} ${animating ? styles.fadeOut : styles.fadeIn}`}
                    >
                        {slide.title}
                    </h3>
                    <p
                        className={`${styles.slideDesc} ${animating ? styles.fadeOut : styles.fadeIn}`}
                    >
                        {slide.description}
                    </p>

                    {/* Dots */}
                    <div className={styles.dots}>
                        {slides.map((_, i) => (
                            <button
                                key={i}
                                className={`${styles.dot} ${i === current ? styles.dotActive : ''}`}
                                onClick={() => goTo(i, i > current ? 'next' : 'prev')}
                                aria-label={`Go to slide ${i + 1}`}
                            ></button>
                        ))}
                    </div>

                    {/* Nav Arrows */}
                    <div className={styles.navRow}>
                        <button className={styles.navBtn} onClick={prev} aria-label="Previous">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 5l-7 7 7 7" />
                            </svg>
                        </button>
                        <span className={styles.counter}>
                            {String(current + 1).padStart(2, '0')}
                            <span className={styles.counterTotal}> / {String(slides.length).padStart(2, '0')}</span>
                        </span>
                        <button className={styles.navBtn} onClick={next} aria-label="Next">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Right: Image Panel */}
                <div className={styles.imagePanel}>
                    <div className={`${styles.imageInner} ${animating ? styles.imgFadeOut : styles.imgFadeIn}`}>
                        <img
                            src={slide.image}
                            alt={slide.title}
                            className={styles.image}
                        />
                        <div className={styles.imgOverlay}></div>
                        <span className={styles.imgCaption}>Artist's Impression</span>
                    </div>

                    {/* Progress bar */}
                    <div className={styles.progressBar}>
                        <div
                            className={styles.progressFill}
                            key={current}
                        ></div>
                    </div>
                </div>

            </div>

        </section>
    )
}

export default AmenitiesCarousel